// ds-typo-ignore-file — WIP page, typography tokens to be normalized before ship
"use client"

import { blobMediaUrl } from "@/lib/blob-url"
import type React from "react"
import { memo, useEffect, useRef, useState } from "react"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { m, AnimatePresence } from "framer-motion"
import { ClientOnly } from "@/lib/animation/client-only"
import { SegmentedBar } from "@/components/ui/segmented-bar"
import { InsuranceCoverageCta } from "@/components/marketing/insurance-coverage-cta"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { ExerciseIcon, NutritionIcon, Report2Icon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { membershipKeys as mk } from "@/i18n/membership-keys"

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="type-label-md mb-3 text-neutral-500">{children}</p>
}

/* Membership year — chronological, every checkpoint visible.
   Figma layout: card-per-step, consistent left-text / right-image.
   Steps 2, 3, 5 include a bullet list of action-plan categories.
   Steps 1, 2 show a "Get started" CTA. */
const JOURNEY_STEP_2_BULLETS = [
  mk.journey2Bullet1,
  mk.journey2Bullet2,
  mk.journey2Bullet3,
]
const JOURNEY_STEP_5_BULLETS = [
  mk.journey5Bullet1,
  mk.journey5Bullet2,
  mk.journey5Bullet3,
]

type JourneyStep = {
  badgeKey: string
  titleKey: string
  descKey: string
  image: string
  bulletKeys?: readonly string[]
  cta?: "member" | "report"
  overlay?:
    | "biomarkers"
    | "action-plan"
    | "appointment"
    | "health-report"
    | "progress-notifications"
    | "expert-support"
    | "trend-chart"
}

const JOURNEY_STEPS: JourneyStep[] = [
  {
    badgeKey: mk.journey1Badge,
    titleKey: mk.journey1Title,
    descKey: mk.journey1Desc,
    image: blobMediaUrl("/images/membership/step1-test.png"),
    cta: "member",
    overlay: "appointment",
  },
  {
    badgeKey: mk.journey2Badge,
    titleKey: mk.journey2Title,
    descKey: mk.journey2Desc,
    image: blobMediaUrl("/images/membership/step2-plan.png"),
    bulletKeys: JOURNEY_STEP_2_BULLETS,
    cta: "report",
    overlay: "health-report",
  },
  {
    badgeKey: mk.journey3Badge,
    titleKey: mk.journey3Title,
    descKey: mk.journey3Desc,
    image: blobMediaUrl("/images/membership/step3-progress.png"),
    overlay: "progress-notifications",
  },
  {
    badgeKey: mk.journey4Badge,
    titleKey: mk.journey4Title,
    descKey: mk.journey4Desc,
    // WIP: placeholder photo, needs a final on-brand shot before launch.
    image: blobMediaUrl("/images/membership/expert-support.png"),
    overlay: "expert-support",
  },
  {
    badgeKey: mk.journey5Badge,
    titleKey: mk.journey5Title,
    descKey: mk.journey5Desc,
    image: blobMediaUrl("/images/membership/stay-on-top-of-changes.png"),
    overlay: "trend-chart",
    bulletKeys: JOURNEY_STEP_5_BULLETS,
  },
]

/* Figma 194:4797 — full-size biomarker card (count, distribution bar, range pills).
   Caller positions via className. */
function BiomarkerCard({ className }: { className?: string }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const segments = [
    {
      value: 12,
      color: "rgba(0,0,0,0.26)",
      opacity: 0.5,
      label: t(mk.overlayBiomarkerChipOptimal),
    },
    {
      value: 67,
      color: "#f8f9ec",
      opacity: 0.4,
      label: t(mk.overlayBiomarkerChipNormal),
    },
    {
      value: 2,
      color: "rgba(0,0,0,0.26)",
      opacity: 0.5,
      label: t(mk.overlayBiomarkerChipOutOfRange),
    },
  ]
  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col gap-[11.369px] rounded-[11.369px] bg-neutral-950 p-[14px] backdrop-blur-[6.905px]",
        className
      )}
    >
      <div className="flex flex-col whitespace-nowrap">
        <span className="text-background/90 text-[38px] leading-[44px] font-normal tracking-[-0.25px]">
          81
        </span>
        <span className="text-[10.565px] leading-[15.093px] text-white/60">
          {t(mk.overlayBiomarkerLabelPlural)}
        </span>
      </div>
      <SegmentedBar
        segments={segments}
        height={6}
        gap={3}
        className="rounded-full"
      />
      <ul className="flex items-center gap-[4.528px]">
        {segments.map((s) => (
          <li
            key={s.label}
            className="inline-flex items-center rounded-[90px] bg-black/[0.11] px-[8px] py-[4px]"
          >
            <span className="text-background/70 text-[9px] leading-[12.075px] font-medium tracking-[0.0755px] whitespace-nowrap">
              {s.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* Figma 195:4817 (transparent outer) / 194:3301 — action plan card with
   three recommendation rows + DS icons. `size="lg"` matches the Figma 195:4817
   frame (transparent outer glass, 12/10px text, 32px icon gutter);
   `size="compact"` (default) is the miniature variant for narrow tiles. */
function ActionPlanCard({
  className,
  size = "compact",
}: {
  className?: string
  size?: "compact" | "lg"
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const items = [
    {
      Icon: NutritionIcon,
      title: t(mk.overlayActionCalciumTitle),
      desc: t(mk.overlayActionCalciumDesc),
    },
    {
      Icon: ExerciseIcon,
      title: t(mk.overlayActionExerciseTitle),
      desc: t(mk.overlayActionExerciseDesc),
    },
    {
      Icon: Report2Icon,
      title: t(mk.overlayActionPotassiumTitle),
      desc: t(mk.overlayActionPotassiumDesc),
    },
  ]
  const isLg = size === "lg"
  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col",
        isLg
          ? "gap-[6px] rounded-[15.065px] p-0"
          : "gap-[6px] rounded-[20px] border-[0.4px] border-white/20 bg-white/20 p-[12px] shadow-none backdrop-blur-[6px]",
        className
      )}
    >
      {items.map(({ Icon, title, desc }) => (
        <div
          key={title}
          className={cn(
            "flex items-center rounded-[10px] bg-neutral-950",
            isLg
              ? "items-start gap-[10.043px] rounded-[10.043px] px-[10.043px] py-[12px]"
              : "gap-[10px] px-[10px] py-[8px]"
          )}
        >
          <div
            className={cn(
              "flex shrink-0 items-center justify-center",
              isLg ? "w-[32px] self-stretch" : "w-[20px]"
            )}
          >
            <Icon size={isLg ? 20 : 18} className="text-white" />
          </div>
          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col",
              isLg ? "gap-[2.511px]" : "gap-[1px]"
            )}
          >
            <p
              className={cn(
                "tracking-[-0.2px] whitespace-nowrap",
                isLg
                  ? "text-[12px] leading-[15.065px] font-medium text-white/80"
                  : "text-[12px] leading-[15px] font-semibold text-white"
              )}
            >
              {title}
            </p>
            <p
              className={cn(
                "truncate",
                isLg
                  ? "text-[10px] leading-[12.554px] font-normal text-white/50"
                  : "text-[11px] leading-[14px] tracking-[-0.2px] text-white/80"
              )}
            >
              {desc}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

/* Figma 257:2168 — "Health report" overlay for step 2 (within days).
   Renders as an iPhone-framed card (5px solid black border, rounded corners)
   containing three stacked widgets:
   - Biological age — dark glass panel, big number, slider + "↓6" bubble marker
   - Heart risk score — dark glass, 4-segment bar + leading white marker
   - Biomarker — light glass, green/olive/terracotta segmented bar + range chips
   The card is positioned anchored to the top of its container so the bottom
   extends below and gets clipped by the container's overflow:hidden, giving
   the effect of an iPhone partially sliding in from the top edge. */
function HealthReportCard({ className }: { className?: string }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col gap-[7px] overflow-hidden rounded-[20px] border border-white/50 bg-white/25 px-[10px] pt-[20px] pb-[12px] font-sans backdrop-blur-[6px]",
        className
      )}
    >
      <p className="text-foreground px-[2px] text-[18px] leading-[1.1]">
        {t(mk.overlayHealthReportTitle)}
      </p>

      {/* Biological age widget — light glass */}
      <div className="flex flex-col gap-[8px] rounded-[8px] border-[0.3px] border-white/40 bg-white/40 p-[10px] backdrop-blur-[5px]">
        <div className="flex flex-col whitespace-nowrap">
          <p className="text-[28px] leading-[32px] font-normal tracking-[-0.2px] text-neutral-800">
            32
          </p>
          <p className="text-[9px] leading-[12px] text-neutral-600/80">
            {t(mk.overlayHealthBiologicalAge)}
          </p>
        </div>
        <div className="relative h-[5px] w-full">
          <div className="h-full w-full rounded-full bg-black/[0.26]" />
          {/* Lollipop: pill head with "↓6" + tail extending ~40px right.
             Tail sits behind the pill so it appears to grow from under it. */}
          <div className="absolute top-1/2 left-[15%] flex -translate-y-1/2 items-center">
            <div className="relative z-10 flex h-[11px] items-center gap-[2px] rounded-full bg-white px-[5px]">
              <svg width="5" height="6" viewBox="0 0 6 7" aria-hidden="true">
                <path
                  d="M3 0v5M1 3.5l2 2 2-2"
                  stroke="#2d2d2d"
                  strokeWidth="0.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              <span className="text-foreground text-[7.5px] leading-none">
                6
              </span>
            </div>
            <div className="-ml-[8px] h-[5px] w-[40px] rounded-full bg-white" />
          </div>
        </div>
        <p className="text-[9px] leading-[12px] text-neutral-600/80">
          {t(mk.overlayHealthBiologicalAgeBody)}
        </p>
      </div>

      {/* Heart risk score widget — light glass */}
      <div className="flex flex-col gap-[8px] rounded-[8px] border-[0.3px] border-white/40 bg-white/40 p-[10px] backdrop-blur-[5px]">
        <div className="flex flex-col whitespace-nowrap">
          <p className="text-[28px] leading-[32px] font-normal tracking-[-0.2px] text-neutral-800">
            1.6%
          </p>
          <p className="text-[9px] leading-[12px] text-neutral-600/80">
            {t(mk.overlayHealthHeartRiskScore)}
          </p>
        </div>
        <p className="text-[9px] leading-[12px] text-neutral-600/80">
          {t(mk.overlayHealthHeartRiskBody)}
        </p>
        <div className="relative flex h-[5px] w-full items-center gap-[2px]">
          <div className="h-full flex-1 rounded-full bg-black/[0.26]" />
          <div className="h-full flex-1 rounded-full bg-black/[0.26]" />
          <div className="h-full flex-1 rounded-full bg-black/[0.26]" />
          <div className="h-full flex-1 rounded-full bg-black/[0.26]" />
          <div className="absolute top-0 left-0 h-[5px] w-[9%] rounded-full bg-white" />
        </div>
      </div>

      {/* Biomarker widget — light glass */}
      <div className="flex flex-col gap-[8px] rounded-[8px] border-[0.3px] border-white/40 bg-white/40 p-[10px] backdrop-blur-[5px]">
        <div className="flex flex-col whitespace-nowrap">
          <p className="text-[28px] leading-[32px] font-normal tracking-[-0.2px] text-neutral-800">
            81
          </p>
          <p className="text-[9px] leading-[12px] text-neutral-600/80">
            {t(mk.overlayBiomarkerLabelPlural)}
          </p>
        </div>
        <div className="flex h-[5px] w-full items-center gap-[2px]">
          <div className="bg-deep-green-500 h-full w-[14.8%] min-w-[8px] shrink-0 rounded-full" />
          <div className="bg-light-green-500 h-full flex-1 rounded-full" />
          <div className="bg-terracotta-500 h-full w-[2.5%] min-w-[8px] shrink-0 rounded-full" />
        </div>
        <div className="flex flex-wrap items-center gap-[3px]">
          <div className="flex shrink-0 items-center rounded-full bg-white px-[7px] py-[3px]">
            <span className="text-[8px] leading-none font-medium tracking-[0.05px] whitespace-nowrap text-neutral-800/70">
              {t(mk.overlayBiomarkerChipOptimal)}
            </span>
          </div>
          <div className="flex shrink-0 items-center rounded-full bg-white px-[7px] py-[3px]">
            <span className="text-[8px] leading-none font-medium tracking-[0.05px] whitespace-nowrap text-neutral-800/70">
              {t(mk.overlayBiomarkerChipNormal)}
            </span>
          </div>
          <div className="flex shrink-0 items-center rounded-full bg-white px-[7px] py-[3px]">
            <span className="text-[8px] leading-none font-medium tracking-[0.05px] whitespace-nowrap text-neutral-800/70">
              {t(mk.overlayBiomarkerChipOutOfRange)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* Figma 197:6173 + 257:3860 — iOS-style push notifications for step 3
   "Track your progress". Two variants:
   - Full: Ahead Health icon + title + body
   - Collapsed: single-line pill */
function ProgressNotificationFull({ className }: { className?: string }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div
      className={cn(
        "pointer-events-none flex items-stretch gap-[9.5px] rounded-[20px] border-[0.4px] border-white/20 bg-white/20 p-[12px] shadow-none backdrop-blur-[6px]",
        className
      )}
    >
      <div className="relative size-[48px] shrink-0 overflow-hidden rounded-[12px]">
        <Image
          src="/icons/membership-bg.png"
          alt=""
          fill
          sizes="48px"
          className="object-cover"
        />
        <Image
          src="/icons/ahead-logo.svg"
          alt=""
          width={28}
          height={28}
          className="absolute inset-0 m-auto h-[22px] w-[22px]"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <p className="text-[13px] leading-[16px] font-semibold tracking-[-0.2px] text-white">
          {t(mk.overlayProgressBrand)}
        </p>
        <p className="text-[12px] leading-[15px] tracking-[-0.2px] text-white">
          {t(mk.overlayProgressBody)}
        </p>
      </div>
    </div>
  )
}

function ProgressNotificationCollapsed({ className }: { className?: string }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div
      className={cn(
        "pointer-events-none flex items-center rounded-[20px] border-[0.4px] border-white/20 bg-white/20 px-[14px] py-[9px] shadow-none backdrop-blur-[6px]",
        className
      )}
    >
      <p className="text-[12px] leading-[15px] tracking-[-0.2px] text-white">
        {t(mk.overlayProgressUpdated)}
      </p>
    </div>
  )
}

function ProgressNotificationsOverlay({ className }: { className?: string }) {
  /* iMessage-style entrance: each bubble pops in at its own slot with a
     spring bounce (scale 0.7 → 1 + fade). Order bottom → top:
       1. Ahead Health full notification (delay 0.3s)
       2. "Your action plan was updated" pill (delay 1.1s)
       3. ActionPlanCard with plan details (delay 1.9s)
     The 8px flex gap keeps them clearly separated so their transparent
     glass backgrounds never overlap mid-animation. */
  const bubble = {
    hidden: { opacity: 0, scale: 0.7 },
    visible: { opacity: 1, scale: 1 },
  }
  const spring = { type: "spring" as const, stiffness: 500, damping: 24 }
  return (
    <div className={cn("flex flex-col gap-[8px]", className)}>
      <m.div
        className="origin-bottom-left"
        variants={bubble}
        initial="hidden"
        animate="visible"
        transition={{ ...spring, delay: 1.9 }}
      >
        <ActionPlanCard size="compact" />
      </m.div>
      <m.div
        className="origin-bottom-left self-start"
        variants={bubble}
        initial="hidden"
        animate="visible"
        transition={{ ...spring, delay: 1.1 }}
      >
        <ProgressNotificationCollapsed />
      </m.div>
      <m.div
        className="origin-bottom-left"
        variants={bubble}
        initial="hidden"
        animate="visible"
        transition={{ ...spring, delay: 0.3 }}
      >
        <ProgressNotificationFull />
      </m.div>
    </div>
  )
}

/* Figma 314:5052 — "Expert support" chat overlay. Two stacked dark glass
   bubbles + a circular avatar with a green status dot. Bubbles stagger in
   bottom→top with a spring bounce, matching the iMessage-style entrance
   used by ProgressNotificationsOverlay. */
function ExpertSupportOverlay({ className }: { className?: string }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const bubble = {
    hidden: { opacity: 0, scale: 0.7 },
    visible: { opacity: 1, scale: 1 },
  }
  const spring = { type: "spring" as const, stiffness: 500, damping: 24 }
  return (
    <div className={cn("flex flex-col items-end gap-[10px]", className)}>
      <m.div
        className="origin-bottom-right"
        variants={bubble}
        initial="hidden"
        animate="visible"
        transition={{ ...spring, delay: 1.1 }}
      >
        <ChatBubble>{t(mk.overlayExpertBubble2)}</ChatBubble>
      </m.div>
      <m.div
        className="origin-bottom-right"
        variants={bubble}
        initial="hidden"
        animate="visible"
        transition={{ ...spring, delay: 0.3 }}
      >
        <ChatBubble>{t(mk.overlayExpertBubble1)}</ChatBubble>
      </m.div>
      <div className="relative size-[40px] shrink-0 overflow-hidden rounded-full shadow-[0_0_0_2px_rgba(255,255,255,0.4)]">
        <Image
          src="/icons/membership-bg.png"
          alt=""
          fill
          sizes="40px"
          className="object-cover"
        />
        <Image
          src="/icons/ahead-logo.svg"
          alt=""
          width={20}
          height={20}
          className="absolute inset-0 m-auto h-[18px] w-[18px]"
        />
      </div>
    </div>
  )
}

function ChatBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="pointer-events-none flex w-[280px] max-w-full items-start rounded-[24px] bg-[rgba(17,17,17,0.56)] px-[14px] py-[12px] backdrop-blur-[3.55px]">
      {" "}
      {/* ds-color-ignore: translucent overlay */}
      <p className="type-body-md text-white">{children}</p>
    </div>
  )
}

/* Figma 314:5055 — Light glass message bubble with the medical advisor's
   name and avatar plus a short note. Floats over the year-after-year
   image alongside the trend chart. */
function DoctorMessageBubble({ className }: { className?: string }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col items-start gap-[8.007px] rounded-[8px] bg-white/80 p-[12.755px]",
        className
      )}
    >
      <div className="flex items-center justify-center gap-[9.566px]">
        <div className="relative size-[25.51px] shrink-0 overflow-hidden rounded-full">
          <Image
            src={blobMediaUrl("/images/doctors/erat.jpeg")}
            alt=""
            fill
            sizes="26px"
            className="object-cover"
          />
        </div>
        <p className="text-foreground text-[11.161px] leading-[15.944px] font-medium tracking-[0.0797px]">
          Dr. med. Anna Erat, PhD
        </p>
      </div>
      <p className="text-foreground w-full text-[11.161px] leading-[15.944px]">
        {t(mk.overlayDoctorMessage)}
      </p>
    </div>
  )
}

/* Figma 257:4474 — "Stay on top of changes" trend card. Shows a single
   biomarker (Fasting Glucose) tracked over five timepoints with a polyline
   chart, a normal-range band on the left axis, and an explainer footer.
   Pure SVG for the chart so it scales cleanly inside the overlay container. */
function FastingGlucoseTrendCard({
  className,
  compact = false,
}: {
  className?: string
  compact?: boolean
}) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  // 5 readings, y = mmol/L on a 0-10 axis.
  const points = [
    { month: "11/24", value: 5.1 },
    { month: "02/25", value: 4.8 },
    { month: "04/25", value: 4.9 },
    { month: "05/25", value: 4.8 },
    { month: "09/25", value: 4.8, current: true },
  ]
  // Normal range band: 3.3-5.6 mmol/L on the y-axis.
  const yMax = 10
  const normalLo = 3.3
  const normalHi = 5.6
  // Chart viewport (in svg units): 200x110 with padded axis area.
  const W = 200
  const H = 110
  const padL = 20
  const padR = 12
  const padT = 8
  const padB = 18
  const chartW = W - padL - padR
  const chartH = H - padT - padB
  const xAt = (i: number) => padL + (chartW * i) / (points.length - 1)
  const yAt = (v: number) => padT + chartH * (1 - v / yMax)
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i)},${yAt(p.value)}`)
    .join(" ")
  const bandTop = yAt(normalHi)
  const bandBottom = yAt(normalLo)

  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col gap-[9.452px] rounded-[16px] border-[0.394px] border-white/15 bg-black/45 p-[13.302px] text-white backdrop-blur-[5.741px]",
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col gap-[6.4px]">
        <div className="flex flex-col gap-[4.267px]">
          <p className="text-[12.8px] leading-none">
            {t(mk.overlayTrendChartTitle)}
          </p>
          <p className="text-[21.333px] leading-none font-medium">4.8</p>
        </div>
        <span className="bg-light-green-500 text-light-green-50 self-start rounded-full px-[4.346px] py-[2px] text-[5px] leading-[5px] font-medium">
          {t(mk.overlayWithinRange)}
        </span>
      </div>

      {/* Chart */}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full overflow-visible"
        aria-hidden="true"
      >
        {/* Y-axis range band on the far left: orange / olive / orange */}
        <rect
          x={3}
          y={padT}
          width={3.2}
          height={bandTop - padT}
          rx={1.6}
          fill="#e96f3f"
          opacity={0.8}
        />
        <rect
          x={3}
          y={bandTop + 1}
          width={3.2}
          height={bandBottom - bandTop - 2}
          rx={1.6}
          fill="#e0e6b0"
          opacity={0.8}
        />
        <rect
          x={3}
          y={bandBottom}
          width={3.2}
          height={H - padB - bandBottom}
          rx={1.6}
          fill="#e96f3f"
          opacity={0.8}
        />

        {/* Y axis labels */}
        {[
          { v: 10, label: "10" },
          { v: 5.6, label: "5.6" },
          { v: 3.3, label: "3.3" },
          { v: 0, label: "0" },
        ].map((t) => (
          <text
            key={t.label}
            x={padL - 2}
            y={yAt(t.v) + 2.2}
            textAnchor="end"
            fill="rgba(255,255,255,0.7)"
            style={{ fontSize: 6.4 }}
          >
            {t.label}
          </text>
        ))}

        {/* Horizontal reference line at upper normal bound (5.6) */}
        <line
          x1={padL}
          x2={W - padR}
          y1={bandTop}
          y2={bandTop}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth={0.5}
          strokeDasharray="1.5 1.5"
        />

        {/* Vertical dotted gridlines per month */}
        {points.map((p, i) => (
          <line
            key={i}
            x1={xAt(i)}
            x2={xAt(i)}
            y1={padT}
            y2={H - padB}
            stroke="rgba(255,255,255,0.35)"
            strokeWidth={0.5}
            strokeDasharray="1.5 1.5"
          />
        ))}

        {/* Trend polyline */}
        <path
          d={linePath}
          fill="none"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth={1}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points + value labels */}
        {points.map((p, i) => {
          const cx = xAt(i)
          const cy = yAt(p.value)
          return (
            <g key={i}>
              {p.current ? (
                <>
                  <circle cx={cx} cy={cy} r={3.5} fill="white" />
                  <circle cx={cx} cy={cy} r={1.6} fill="#3a4a1a" />
                </>
              ) : (
                <circle cx={cx} cy={cy} r={3.2} fill="white" />
              )}
              {!compact && (
                <text
                  x={cx}
                  y={cy + 9}
                  textAnchor="middle"
                  fill="white"
                  style={{ fontSize: 6.4, fontWeight: 500, lineHeight: "10px" }}
                >
                  {p.value.toFixed(1)}
                </text>
              )}
            </g>
          )
        })}

        {/* Month labels along x axis */}
        {!compact &&
          points.map((p, i) => (
            <text
              key={p.month}
              x={xAt(i)}
              y={H - 3}
              textAnchor="middle"
              fill="white"
              style={{ fontSize: 6.4 }}
            >
              {p.month}
            </text>
          ))}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-[6px] gap-y-[4.267px]">
        <span className="inline-flex items-center gap-[4.267px] text-[6.4px] leading-[8.533px] text-white">
          <span className="bg-terracotta-400 size-[6.933px] rounded-full" />
          {t(mk.overlayTrendLegendIntermediate)}
        </span>
        <span className="inline-flex items-center gap-[4.267px] text-[6.4px] leading-[8.533px] text-white">
          <span className="bg-light-green-200 size-[6.933px] rounded-full" />
          {t(mk.overlayTrendLegendNormalRange)}
        </span>
      </div>

      {/* Summary */}
      <div className="flex flex-col gap-[2.133px] text-white">
        <p className="text-[9.6px] leading-none font-medium">
          {t(mk.overlayTrendExplainerHeading)}
        </p>
        <p className="text-[8px] leading-[1.3]">
          {t(mk.overlayTrendExplainerBody)}
        </p>
      </div>
    </div>
  )
}

/* Figma 257:2903 — "Advanced blood test" appointment confirmation card.
   Transparency + sizing match Figma exactly: outer glass at rgba(255,255,255,0.2)
   with a 0.4px white/30% stroke, inner cards at rgba(255,255,255,0.3),
   Confirmed pill at rgba(0,0,0,0.08). */
function AppointmentCard({ className }: { className?: string }) {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  return (
    <div
      className={cn(
        "pointer-events-none flex flex-col gap-[9.452px] rounded-[9.452px] border-[0.4px] border-white/30 bg-white/20 p-[13.302px] backdrop-blur-[5.741px]",
        className
      )}
    >
      {/* Header — main thumb with stacked add-on tiles peeking behind.
         All three tiles are the same 53.209px (matches Figma 257:2908-2910).
         Heavy -ml overlap keeps the stack compact so only a narrow sliver
         of each tile is visible beyond the main thumb's right edge. */}
      <div className="flex items-center gap-[9.977px]">
        <div className="flex shrink-0 items-center">
          <div className="relative z-30 size-[53.209px] shrink-0 overflow-hidden rounded-[11.64px] border-[1.663px] border-white">
            <Image
              src={blobMediaUrl("/images/membership/blood-thumb.png")}
              alt=""
              fill
              sizes="54px"
              className="object-cover"
            />
          </div>
          <div className="z-20 -ml-[49px] size-[53.209px] shrink-0 rounded-[11.64px] border-[1.663px] border-white bg-[#D9928A]" />
          <div className="bg-error-300 z-10 -ml-[49px] size-[53.209px] shrink-0 rounded-[11.64px] border-[1.663px] border-white" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[1.663px]">
          <p className="text-foreground text-[13.302px] leading-[1.1] font-medium">
            {t(mk.memberPricingItemAdvancedBlood)}
          </p>
          <p className="text-foreground text-[10.808px] leading-[1.3]">
            {t(mk.overlayApptAddons)}
          </p>
        </div>
      </div>

      {/* Date row */}
      <div className="flex items-center gap-[9.977px] rounded-[6.651px] bg-white/30 p-[13.302px]">
        <div
          className="flex size-[33.256px] shrink-0 items-center justify-center rounded-[6.651px]"
          style={{
            background:
              "linear-gradient(152.755deg, rgba(147, 182, 238, 0.2) 0%, rgba(81, 130, 208, 0.2) 100%), #fff",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="3"
              y="5"
              width="18"
              height="16"
              rx="2"
              stroke="#5A6A9C"
              strokeWidth="1.5"
            />
            <path
              d="M3 10h18"
              stroke="#5A6A9C"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M8 3v4M16 3v4"
              stroke="#5A6A9C"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[3.326px]">
          <p className="text-foreground text-[11.64px] leading-none">
            {t(mk.overlayApptDate)}
          </p>
          <p className="text-foreground text-[9.977px] leading-[1.2]">
            {t(mk.overlayApptTime)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-[3.326px] rounded-[40px] bg-black/[0.08] py-[3.326px] pr-[8.314px] pl-[4.988px]">
          <svg
            width="9"
            height="9"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3.5 8.5l3 3 6-7"
              stroke="#2d2d2d"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-foreground text-[9.98px] leading-none">
            {t(mk.overlayApptConfirmed)}
          </span>
        </div>
      </div>

      {/* Location row */}
      <div className="flex items-center gap-[9.977px] rounded-[6.651px] bg-white/30 p-[13.302px]">
        <div className="relative size-[33.256px] shrink-0 overflow-hidden rounded-[6.651px] bg-white">
          <Image
            src={blobMediaUrl("/images/membership/zurich-thumb.png")}
            alt=""
            fill
            sizes="34px"
            className="object-cover"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[3.326px]">
          <p className="text-foreground text-[11.64px] leading-none">Zürich</p>
          <p className="text-foreground truncate text-[9.977px] leading-[1.2]">
            Witellikerstrasse 40, 8032 Zürich
          </p>
        </div>
      </div>

      {/* Actions row — extra top margin to visually center it between the
         location card and the outer card's bottom padding. */}
      <div className="mt-[3.85px] flex items-center justify-center gap-[19.953px]">
        <div className="flex items-center gap-[4.988px] text-black opacity-80">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9 4l-6 2v14l6-2 6 2 6-2V4l-6 2-6-2z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <path d="M9 4v14M15 6v14" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <span className="text-[10.808px] leading-[1.1]">
            {t(mk.overlayApptGetDirections)}
          </span>
        </div>
        <div className="h-[9.977px] w-px bg-black/20" />
        <div className="flex items-center gap-[4.988px] text-black opacity-80">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="13"
              r="8"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M12 9v4l2.5 2.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M5 5l3-2M19 5l-3-2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-[10.808px] leading-[1.1]">
            {t(mk.overlayApptAddToCalendar)}
          </span>
        </div>
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────
   4. Membership year — merged timeline
   Image per step, all tier markers shown
   ──────────────────────────────────────────── */

/* Single segment of the membership-year progress indicator — fills from
   left as scroll progress passes through its [from, to] range. */
/* Pill-and-dots progress indicator: active step renders as a pill,
   others as neutral dots. Smooth layout morph when active changes. */
const ProgressDot = memo(
  function ProgressDot({
    isActive,
    onClick,
    ariaLabel,
  }: {
    isActive: boolean
    onClick: () => void
    ariaLabel: string
  }) {
    return (
      <m.button
        layout
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className="h-[8px] cursor-pointer rounded-full will-change-transform"
        animate={{
          width: isActive ? 32 : 8,
          backgroundColor: isActive ? "#d4d4d4" : "#e5e5e5",
        }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      />
    )
  },
  // onClick is a fresh closure each render but always does the same thing
  // (scroll to this step); only isActive matters for visual output.
  (prev, next) => prev.isActive === next.isActive
)

/* Deep-green check — DS status color #408c49, swapped in for GreenCheck
   in the membership-year bullet lists per Jana's spec. */
function DeepGreenCheck({
  size = 20,
  className,
}: {
  size?: number
  className?: string
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7.5" fill="#408c49" />
      <path
        d="M5 8l2 2 4-4"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function MembershipYear() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const lp = useLocalePath()
  const [activeIndex, setActiveIndex] = useState(0)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])
  const dotRefs = useRef<(HTMLDivElement | null)[]>([])
  const imageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // A step becomes active only while its dot is inside the image's vertical
    // band — so the image swaps exactly when the dot crosses the image's
    // bottom edge (scrolling down) or top edge (scrolling up). rAF-throttled
    // and guarded against no-op state updates to keep scroll at 60fps.
    let rafId = 0
    let lastActive = -1

    const compute = () => {
      rafId = 0
      const img = imageRef.current
      if (!img) return
      const imgRect = img.getBoundingClientRect()
      const vh = window.innerHeight
      // Bail out if the image band is fully off-screen — no dot can be inside.
      if (imgRect.bottom < 0 || imgRect.top > vh) return
      const imgTop = imgRect.top
      const imgBottom = imgRect.bottom
      for (let i = 0; i < dotRefs.current.length; i++) {
        const dot = dotRefs.current[i]
        if (!dot) continue
        const dotRect = dot.getBoundingClientRect()
        const dotY = dotRect.top + dotRect.height / 2
        if (dotY >= imgTop && dotY <= imgBottom) {
          if (lastActive !== i) {
            lastActive = i
            setActiveIndex(i)
          }
          return
        }
      }
    }

    const onScroll = () => {
      if (rafId === 0) rafId = requestAnimationFrame(compute)
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    // Initial position (and on resize, since the image band changes).
    compute()
    window.addEventListener("resize", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (rafId !== 0) cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <section id="membership-year" className="bg-neutral-50 pt-28 pb-0">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="mx-auto mb-4 max-w-[620px] text-center md:mb-6">
          <SectionLabel>{t(mk.yearLabel)}</SectionLabel>
          <h2 className="type-display-md mb-3.5 text-neutral-900">
            {t(mk.yearHeading)}
          </h2>
          <p className="type-body-lg text-neutral-600">
            {t(mk.yearDescription)}
          </p>
        </div>

        {/* Grid matches the 1440px navigation max-width. Balanced
            image↔line and line↔text spacing of 64px each. */}
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-16">
          {/* Sticky image + progress pills — vertically centered in viewport */}
          <div className="hidden md:block">
            <div className="sticky top-0 flex min-h-screen flex-col justify-center py-8">
              <div
                ref={imageRef}
                className="relative aspect-[8/7] w-full overflow-hidden rounded-[24px] bg-neutral-100"
              >
                {JOURNEY_STEPS.map((s, i) => (
                  <m.div
                    key={i}
                    className="absolute inset-0 will-change-[opacity,transform]"
                    initial={false}
                    animate={{
                      opacity: i === activeIndex ? 1 : 0,
                      scale: i === activeIndex ? 1 : 1.02,
                    }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Image
                      src={s.image}
                      alt=""
                      fill
                      sizes="(max-width: 768px) 100vw, 45vw"
                      className="object-cover"
                      priority={i === 0}
                    />
                  </m.div>
                ))}
                {/* Overlays hoisted out of the opacity-animating step wrapper
                    so backdrop-filter keeps working (Chrome disables it when
                    an ancestor has opacity<1 or a running transform). */}
                <ClientOnly>
                  <AnimatePresence mode="sync">
                    {JOURNEY_STEPS[activeIndex]?.overlay === "biomarkers" && (
                      <m.div
                        key={`bio-${activeIndex}`}
                        initial={{ y: 20 }}
                        animate={{ y: 0 }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute top-[40px] left-[40px] w-[260px]"
                      >
                        <BiomarkerCard />
                      </m.div>
                    )}
                    {JOURNEY_STEPS[activeIndex]?.overlay ===
                      "health-report" && (
                      <m.div
                        key={`report-${activeIndex}`}
                        initial={{ y: 20 }}
                        animate={{ y: 0 }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute bottom-[20px] left-1/2 w-[250px] -translate-x-1/2"
                      >
                        <div className="origin-top scale-[1.1]">
                          <HealthReportCard className="h-[423px]" />
                        </div>
                      </m.div>
                    )}
                    {JOURNEY_STEPS[activeIndex]?.overlay === "appointment" && (
                      <m.div
                        key={`appt-${activeIndex}`}
                        initial={{ y: 20 }}
                        animate={{ y: 0 }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute bottom-[40px] left-[40px] w-[320px]"
                      >
                        <AppointmentCard />
                      </m.div>
                    )}
                    {JOURNEY_STEPS[activeIndex]?.overlay ===
                      "progress-notifications" && (
                      <ProgressNotificationsOverlay
                        key={`notif-${activeIndex}`}
                        className="absolute bottom-[40px] left-[40px] w-[300px]"
                      />
                    )}
                    {JOURNEY_STEPS[activeIndex]?.overlay ===
                      "expert-support" && (
                      <ExpertSupportOverlay
                        key={`expert-${activeIndex}`}
                        className="absolute right-[20px] bottom-[20px]"
                      />
                    )}
                    {JOURNEY_STEPS[activeIndex]?.overlay === "trend-chart" && (
                      <m.div
                        key={`trend-${activeIndex}`}
                        initial={{ y: 20 }}
                        animate={{ y: 0 }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute top-[calc(45%-50px)] left-[40px] w-[200px]"
                      >
                        <div className="origin-top-left scale-[1.5]">
                          <FastingGlucoseTrendCard />
                        </div>
                      </m.div>
                    )}
                    {JOURNEY_STEPS[activeIndex]?.overlay === "trend-chart" && (
                      <m.div
                        key={`donati-${activeIndex}`}
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.4,
                        }}
                        className="absolute top-[407px] left-[219px] w-[260px] backdrop-blur-[2.4px]"
                      >
                        <DoctorMessageBubble />
                      </m.div>
                    )}
                  </AnimatePresence>
                </ClientOnly>
              </div>

              {/* Pill + dots indicator — centered, 25% width, subtle neutrals.
                  Active step morphs into a pill; others stay as dots. */}
              <div className="mx-auto mt-6 flex w-1/4 items-center justify-center gap-2">
                {JOURNEY_STEPS.map((_, i) => (
                  <ProgressDot
                    key={i}
                    isActive={i === activeIndex}
                    ariaLabel={t(mk.yearProgressDotAria)}
                    onClick={() =>
                      stepRefs.current[i]?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      })
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Text column — single solid light vertical line, fades at ends only */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute top-0 bottom-0 left-0 hidden w-px bg-neutral-100 md:block"
              style={{
                maskImage:
                  "linear-gradient(to bottom, transparent 0%, black 6%, black 94%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent 0%, black 6%, black 94%, transparent 100%)",
              }}
            />

            {JOURNEY_STEPS.map((s, i) => (
              <div
                key={i}
                data-step-idx={i}
                ref={(el) => {
                  stepRefs.current[i] = el
                }}
                // Each step is 1.28× viewport tall — the inner sticky wrapper
                // pins the text at center for the middle of that range,
                // giving a short dwell before the content scrolls away and
                // the next step takes over.
                className="relative py-10 md:min-h-[128vh] md:py-0"
              >
                <div className="md:sticky md:top-0 md:flex md:h-screen md:flex-col md:justify-center">
                  {/* Mobile-only inline image */}
                  <div className="@container relative mb-6 aspect-square w-full overflow-hidden rounded-[24px] bg-neutral-100 md:hidden">
                    <Image
                      src={s.image}
                      alt=""
                      fill
                      sizes="100vw"
                      className="object-cover"
                    />
                    {s.overlay === "biomarkers" && (
                      <m.div
                        initial={{ y: 20 }}
                        whileInView={{ y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute bottom-[16px] left-[16px] max-w-[calc(100%-32px)]"
                      >
                        <BiomarkerCard />
                      </m.div>
                    )}
                    {s.overlay === "health-report" && (
                      <m.div
                        initial={{ x: "-50%", y: 20 }}
                        whileInView={{ x: "-50%", y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute top-[29px] left-1/2 w-[240px] max-w-[calc(100%-32px)]"
                      >
                        <HealthReportCard />
                      </m.div>
                    )}
                    {s.overlay === "appointment" && (
                      <m.div
                        initial={{ y: 20 }}
                        whileInView={{ y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute bottom-[16px] left-[16px] w-[300px] max-w-[calc(100%-32px)]"
                      >
                        <AppointmentCard />
                      </m.div>
                    )}
                    {s.overlay === "progress-notifications" && (
                      <m.div
                        initial={{ y: 20 }}
                        whileInView={{ y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                          duration: 0.9,
                          ease: [0.16, 1, 0.3, 1],
                          delay: 0.15,
                        }}
                        className="absolute bottom-[16px] left-[16px] w-[280px] max-w-[calc(100%-32px)]"
                      >
                        <ProgressNotificationsOverlay />
                      </m.div>
                    )}
                    {s.overlay === "expert-support" && (
                      <ExpertSupportOverlay className="absolute right-[16px] bottom-[16px] max-w-[calc(100%-32px)]" />
                    )}
                    {s.overlay === "trend-chart" && (
                      <>
                        <m.div
                          initial={{ y: 20 }}
                          whileInView={{ y: 0 }}
                          viewport={{ once: true, margin: "-80px" }}
                          transition={{
                            duration: 0.9,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.15,
                          }}
                          className="absolute top-[11cqw] left-[5cqw] w-[47cqw]"
                        >
                          <div className="origin-top-left scale-[1.25]">
                            <FastingGlucoseTrendCard compact />
                          </div>
                        </m.div>
                        <m.div
                          initial={{ y: -20, opacity: 0 }}
                          whileInView={{ y: 0, opacity: 1 }}
                          viewport={{ once: true, margin: "-80px" }}
                          transition={{
                            duration: 0.9,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.4,
                          }}
                          className="absolute right-[5cqw] bottom-[5cqw] w-[58cqw] backdrop-blur-[1.993px]"
                        >
                          <DoctorMessageBubble />
                        </m.div>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col gap-4 md:gap-5 md:pl-16">
                    <span className="type-label-lg bg-deep-green-100 text-deep-green-900 inline-flex self-start rounded-full px-3 py-1 normal-case">
                      {t(s.badgeKey)}
                    </span>

                    {/* Title + small neutral dot matching the progress indicator
                      palette. Aligned with the first-line center of the
                      title via lh-based top; pulses softly when active. */}
                    <div
                      className="relative"
                      style={{ lineHeight: "var(--leading-display-md)" }}
                    >
                      <m.div
                        ref={(el) => {
                          dotRefs.current[i] = el
                        }}
                        aria-hidden="true"
                        className="absolute left-[-67px] hidden h-[8px] w-[8px] rounded-full ring-4 ring-neutral-50 md:block"
                        style={{ top: "calc(0.5lh - 4px)" }}
                        animate={
                          i === activeIndex
                            ? {
                                backgroundColor: "var(--color-neutral-200)",
                                scale: [1, 1.3, 1],
                              }
                            : {
                                backgroundColor: "var(--color-neutral-100)",
                                scale: 1,
                              }
                        }
                        transition={
                          i === activeIndex
                            ? {
                                backgroundColor: {
                                  duration: 0.4,
                                  ease: [0.16, 1, 0.3, 1],
                                },
                                scale: {
                                  duration: 1.6,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                },
                              }
                            : { duration: 0.4, ease: [0.16, 1, 0.3, 1] }
                        }
                      />
                      <h3 className="type-display-md text-neutral-900">
                        {t(s.titleKey)}
                      </h3>
                    </div>

                    <p className="type-body-lg max-w-[480px] text-neutral-600">
                      {t(s.descKey)}
                    </p>

                    {s.bulletKeys && (
                      <ul className="mt-1 flex flex-col gap-3">
                        {s.bulletKeys.map((bk) => (
                          <li key={bk} className="flex items-center gap-3">
                            <DeepGreenCheck size={20} className="shrink-0" />
                            <span className="type-body-lg text-neutral-700">
                              {t(bk)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {s.cta === "member" && (
                      // Insurance-coverage CTA (CH-only — renders nothing in
                      // other countries). Replaces the prior "Become a member"
                      // button in this journey step. Reuses the already-approved
                      // `checkInsuranceCoverage` string.
                      <InsuranceCoverageCta
                        size="md"
                        icon="heart-check-pulse"
                        // pl-0 strips the md variant's left padding so the icon
                        // aligns flush with the step heading/body text;
                        // hover:bg-transparent drops the link-wide hover fill.
                        className="mt-2 self-start pl-0 hover:bg-transparent"
                      />
                    )}
                    {s.cta === "report" && (
                      <a
                        href={lp("/report")}
                        className={cn(
                          buttonVariants({ variant: "outline" }),
                          "mt-2 self-start"
                        )}
                      >
                        {t(mk.journeyCtaReport)}
                        <ArrowRightIcon className="ml-2" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
